/**
 * SignInRules (SETTINGS-ACCESS-01) — the company-wide sign-in rules, on the
 * People & Access page: the invite switch + link domains (TEAM-PROVISION),
 * two-factor required for admins (AUTH-MFA-01), and the access log. Your OWN
 * two-factor lives in the user menu ("My sign-in").
 */
import { useEffect, useState } from "react";
import { Check, KeyRound, Loader2, ScrollText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import { RequireForAdminsCard } from "../SignInSecuritySection";
import { ACCESS_EVENTS_KEY, type AccessEvent } from "./access";

// TEAM-PROVISION — the go-live switch for staff invites + the app URL invite/
// reset links point at. Held (off) until Pegasus is deployed; mirrors the
// comms/payments kill-switch pattern.
export function InviteGate() {
  const liveQ = useOrgSettingJson<boolean>("team_invites_live", false);
  const urlQ = useOrgSettingJson<string>("app_url", "https://app.sedgwickheating.com");
  // SETTINGS-AUDIT-01: there are TWO link domains and they were easy to
  // confuse — app_url (the STAFF app, invite/reset links) and public_app_url
  // (CUSTOMER links: portal, payments, proposals). Both editable, both labeled.
  const pubQ = useOrgSettingJson<string>("public_app_url", "https://my.sedgwickheating.com");
  const save = useSaveOrgSetting();
  const isLive = liveQ.data ?? false;
  const [appUrl, setAppUrl] = useState("");
  const [pubUrl, setPubUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState(false);
  useEffect(() => { if (urlQ.data != null) setAppUrl(urlQ.data); }, [urlQ.data]);
  useEffect(() => { if (pubQ.data != null) setPubUrl(pubQ.data); }, [pubQ.data]);
  const urlDirty = appUrl.trim() !== (urlQ.data ?? "");
  const pubDirty = pubUrl.trim() !== (pubQ.data ?? "");

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><KeyRound className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              Logins &amp; Invites
              <InfoTip label="About logins and invites">
                Create staff logins on the People tab. Invite and password-reset emails stay held until you turn this on at rollout.
              </InfoTip>
            </h3>
            <button type="button" onClick={() => save.mutate({ key: "team_invites_live", value: !isLive })} disabled={save.isPending}
              className={cn("relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors", isLive ? "bg-emerald-500" : "bg-slate-300")}
              role="switch" aria-checked={isLive} title={isLive ? "Invites are live" : "Invites are held"}>
              <span className={cn("inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform", isLive ? "translate-x-5" : "translate-x-0.5")} />
            </button>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Invite and password-reset emails are{" "}
            <strong className={isLive ? "text-emerald-700" : "text-amber-700"}>{isLive ? "LIVE — sending for real" : "HELD — nothing sends"}</strong>.
          </p>
          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] font-medium text-slate-500">Staff app URL — where invite &amp; password-reset links land</span>
            <div className="flex items-center gap-2">
              <input value={appUrl} onChange={(e) => { setAppUrl(e.target.value); setSavedUrl(false); }} placeholder="https://app.sedgwickheating.com"
                className="h-9 flex-1 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
              <button type="button" disabled={!urlDirty || save.isPending} onClick={() => save.mutate({ key: "app_url", value: appUrl.trim() }, { onSuccess: () => setSavedUrl(true) })}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50">
                {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
              </button>
            </div>
          </label>
          <label className="mt-2 block">
            <span className="mb-1 block text-[11px] font-medium text-slate-500">Customer link domain — portal, payment &amp; proposal links customers receive</span>
            <div className="flex items-center gap-2">
              <input value={pubUrl} onChange={(e) => { setPubUrl(e.target.value); setSavedUrl(false); }} placeholder="https://my.sedgwickheating.com"
                className="h-9 flex-1 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
              <button type="button" disabled={!pubDirty || save.isPending} onClick={() => save.mutate({ key: "public_app_url", value: pubUrl.trim() }, { onSuccess: () => setSavedUrl(true) })}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50">
                {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
              </button>
            </div>
            {savedUrl && <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-600"><Check className="h-3 w-3" /> Saved</span>}
            {save.isError && <span className="mt-1 block text-[11px] font-medium text-rose-600">That didn&apos;t save — try again.</span>}
          </label>
        </div>
      </div>
    </Card>
  );
}


function useRecentAccessEvents() {
  return useQuery({
    queryKey: [...ACCESS_EVENTS_KEY, "recent"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AccessEvent[]> => {
      const { data, error } = await supabase.from("pegasus_access_event").select("*").order("created_at", { ascending: false }).limit(30);
      if (error) throw error;
      return (data ?? []) as AccessEvent[];
    },
  });
}

const ACTION_WORDS: Record<string, string> = {
  create_and_invite: "created a login + emailed the invite", resend_invite: "re-sent the invite", send_reset: "emailed a password reset",
  create_with_password: "created a login with a temporary password", set_temp_password: "set a temporary password",
  remove_mfa: "removed the authenticator", disable_login: "disabled the login", enable_login: "re-enabled the login",
};

export function AccessLog() {
  const { data: events, isLoading } = useRecentAccessEvents();
  const fmt = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <Card className="p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <ScrollText className="h-4 w-4 text-brand-600" /> Access Log
        <InfoTip label="About the access log">Every change to a login: who did it, to whom and when. The Audit Log has these too.</InfoTip>
      </h3>
      {isLoading ? (
        <p className="mt-3 text-xs text-slate-400">Loading…</p>
      ) : (events ?? []).length === 0 ? (
        <p className="mt-3 text-xs text-slate-400">Nothing yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 text-xs">
          {(events ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 py-1.5">
              <span className="text-slate-400">{fmt(e.created_at)}</span>
              <span className="font-medium text-slate-800">{(e.actor_email ?? "someone").split("@")[0]}</span>
              <span className="text-slate-600">{ACTION_WORDS[e.action] ?? e.action}</span>
              {e.target_email && <span className="text-slate-500">for {e.target_email}</span>}
              {e.detail && <span className="text-slate-400">— {e.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function SignInRules() {
  return (
    <div className="space-y-4">
      <RequireForAdminsCard />
      <InviteGate />
      <AccessLog />
    </div>
  );
}
